import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';

// Material ui
import { Grid, TextField, InputAdornment } from '@material-ui/core';

// Components
import Typography from '@components/UI-KIT/Typography';

// Styles
import '../Inputs.scss';

// icons
import { FaEye, FaEyeSlash } from 'react-icons/fa';

interface Props {
    value: string;
    onChange: (value: string) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    name?: string;
    id?: string;
    optional?: boolean;
    variant?: string;
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    errorCase?: boolean;
    errorLabel?: string;
    errorCase2?: boolean;
    errorLabel2?: string;
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const PasswordInput: React.FC<Props> = ({
    value,
    variant = 'primary',
    onChange,
    autoFocus = false,
    disabled = false,
    label = '',
    placeholder,
    name,
    id,
    optional = false,
    md,
    lg,
    sm,
    xs,
    errorCase = false,
    errorLabel = '',
    errorCase2 = false,
    errorLabel2 = '',
    onKeyPress
}) => {
    const [ _inputError, setError ] = useState(false);
    const [ showpass, setPass ] = useState(false);
    const inputRef = useRef<HTMLInputElement>();
    const idRef = useRef(uuid());

    const validateInput = (inputValue: string) => {
        if (optional) return;
        if (inputValue.length === 0) setError(true);
        else setError(false);
    };

    // Handle focus event
    useEffect(() => {
        const handleFocus = (e: FocusEvent) => {
            const target = (e.target as HTMLInputElement);
            if (target && target.parentElement)
                target.parentElement.parentElement.id = target.parentElement.parentElement.id === 'ui-Input-focused' ? idRef.current : 'ui-Input-focused';
        };
        if (!inputRef.current) return;
        inputRef.current.addEventListener('focus', handleFocus);
        inputRef.current.addEventListener('blur', handleFocus);
    }, [ inputRef ]);

    return (
        <Grid
            className="ui-PasswordInput"
            item
            md={md}
            lg={lg}
            sm={sm}
            xs={xs === 1 ? undefined : xs}
            style={{
                width: xs === 1 ? '6rem' : ''
            }}
        >
            <Typography
                variant="input-head"
                className="ui-Input-label"
                color={disabled ? 'content-light' : 'primary-dark'}
            >
                {`${label}${optional ? ' (opcional)' : ''}`}
            </Typography>
            <TextField
                value={value}
                type={showpass ? 'text' : 'password'}
                id={id}
                onKeyPress={onKeyPress}
                className={`ui-Input-container ${variant}${false ? ' error-border' : ''}`}
                name={name}
                autoFocus={autoFocus}
                disabled={disabled}
                placeholder={placeholder}
                size="small"
                variant="outlined"
                onChange={({ target }) => {
                    onChange(target.value);
                    validateInput(target.value);
                }}
                InputProps={{
                    ref: inputRef,
                    endAdornment: (
                        <InputAdornment
                            className="ui-PasswordInput-EyeIcon"
                            position="end"
                            onClick={() => setPass(!showpass)}
                        >
                            { showpass ? <FaEye /> : <FaEyeSlash /> }
                        </InputAdornment>
                    )
                }}
            />
            {errorCase && (
                <Typography
                    variant="note-regular"
                    color="error"
                    className="ui-Error-msg"
                >
                    {errorLabel}
                </Typography>
            )}
            {errorCase2 && (
                <Typography
                    variant="note-regular"
                    color="error"
                    className="ui-Error-msg"
                >
                    {errorLabel2}
                </Typography>
            )}
        </Grid>
    );
};

export default PasswordInput;
